// Near-field stones (grass.js scatter, the `rocks` mesh): ONE InstancedMesh,
// one draw. An instance is a CLUMP of up to CLUMP_STONES rocks; the clump
// geometry merges STONE_SHAPES rock meshes made here (seeded, deterministic:
// a jittered noise-displaced ellipsoid, 2-3 fracture planes, its convex hull —
// angular, flat-shaded, <= STONE_TRIS_CAP tris each). Per instance the vertex
// shader poses every stone by an integer hash of the instance seed (yaw, tilt,
// size, offset — stonePose is its exact JS twin) and collapses stones k >=
// the instance's count to a point (zero-area, never rasterised).
//
// LIGHT like the ground (APP_SURFACE §Light): MeshBasic, no scene light, the
// material colour = meta.tex.light.white (the terrain's gain), ONE display
// curve. A stone face's radiance = the ground's baked colour at the stone's
// foot x W x E(face N) / E(the ground's baked N), E = the bake's own model
// (APP_GROUNDMAT "Relight": gnd + (sky - gnd)(0.5 N.y + 0.5) + sun vis
// max(N.L, 0)), vis = the foot's sun visibility (surface/shade R). So a
// face like the ground's is exactly the ground, a sun face brighter, the far
// face the ground's shade. The baked colour is the GROUND-M texture pool
// (gmtex.js GmTex.uniforms, shared read-only: the generated tiles the ground
// draws, groundgen.js), a coarse mip (~STONE_TEXEL_M) at the foot; where the pool holds no
// tile there (or no pool is wired yet) the stone is not drawn — no fallback
// colour (gs-family-rock deleted the class palette's).
import * as THREE from 'three';
import { ConvexHull } from 'three/examples/jsm/math/ConvexHull.js';

export const STONE_SHAPES = 5;       // rock meshes
export const CLUMP_STONES = 5;       // stones per clump (k = 0 the large centre one)
export const STONE_TRIS_CAP = 64;    // per rock mesh (the hull of STONE_PTS points: <= 2 n - 4)
const STONE_PTS = 34;
export const STONE_TEXEL_M = 1.5;    // the ground colour averaged over ~ this footprint
const STONE_TILT = 0.55;             // a stone's random tilt, +- rad / 2 (~16 deg)
const STONE_JIT_M = 0.3;             // a stone's offset jitter in the clump, +- m / 2
const STONE_SIZE = [0.7, 1.3];       // a stone's size factor per instance
// the clump layout (clump-local m before the instance scale): [x, z, size m,
// buried fraction of its height]; k = 0 the centre block, then satellites
const LAYOUT = [
  [0, 0, 0.72, 0.32],
  [0.62, 0.18, 0.42, 0.38],
  [-0.38, 0.58, 0.34, 0.3],
  [-0.52, -0.42, 0.3, 0.4],
  [0.22, -0.7, 0.26, 0.34],
];

// mulberry32 (the shapes' seeded draw)
function prng(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// one rock: unit-ish block (|x|, |z| <= ~0.5, height ~0.55), centred, flat
// faces -> {pos: Float32Array (non-indexed), nrm, tone (per vertex, per face
// constant), h (its height)}
export function rockShape(i) {
  const r = prng(0x51ab + i * 7919);
  const ax = 0.42 + r() * 0.1, ay = 0.24 + r() * 0.1, az = 0.34 + r() * 0.1;
  const pts = [];
  for (let k = 0; k < STONE_PTS; k++) {
    // jittered Fibonacci sphere, radius noise 0.82..1.12
    const t = (k + 0.5) / STONE_PTS, y = 1 - 2 * t, rr = Math.sqrt(Math.max(0, 1 - y * y));
    const ph = k * 2.39996 + (r() - 0.5) * 0.5;
    const s = 0.82 + r() * 0.3;
    pts.push(new THREE.Vector3(Math.cos(ph) * rr * ax * s, y * ay * s, Math.sin(ph) * rr * az * s));
  }
  // fracture planes: points past a plane are pressed onto it (flat facets)
  const nPl = 2 + Math.floor(r() * 2);
  for (let p = 0; p < nPl; p++) {
    const th = r() * Math.PI * 2, el = (r() - 0.3) * 1.2;
    const n = new THREE.Vector3(Math.cos(th) * Math.cos(el), Math.sin(el), Math.sin(th) * Math.cos(el));
    const d = 0.62 + r() * 0.18;
    for (const q of pts) {
      const ext = Math.abs(n.x) * ax + Math.abs(n.y) * ay + Math.abs(n.z) * az;
      const o = q.dot(n) - d * ext;
      if (o > 0) q.addScaledVector(n, -o);
    }
  }
  const hull = new ConvexHull().setFromPoints(pts);
  const pos = [], nrm = [], tone = [];
  let y0 = Infinity, y1 = -Infinity;
  for (const f of hull.faces) {
    const tn = 0.9 + r() * 0.16;
    let e = f.edge;
    do {
      const p = e.head().point;
      pos.push(p.x, p.y, p.z);
      nrm.push(f.normal.x, f.normal.y, f.normal.z);
      tone.push(tn);
      y0 = Math.min(y0, p.y); y1 = Math.max(y1, p.y);
      e = e.next;
    } while (e !== f.edge);
  }
  // centre the block vertically (layout burial is a fraction of h)
  const yc = (y0 + y1) / 2;
  for (let k = 1; k < pos.length; k += 3) pos[k] -= yc;
  return { pos: new Float32Array(pos), nrm: new Float32Array(nrm), tone: new Float32Array(tone), h: y1 - y0 };
}

// the clump: every stone k of LAYOUT, shape k % STONE_SHAPES, scaled to its
// size, its centre sunk so `bury` of its height is below y = 0. Attributes:
// position, normal, aTone (per face), aStone (its centre x, y, z + k)
export function makeClumpGeometry() {
  const P = [], N = [], T = [], S = [];
  const tris = [];
  LAYOUT.forEach(([x, z, sz, bury], k) => {
    const sh = rockShape(k % STONE_SHAPES);
    tris.push(sh.pos.length / 9);
    const cy = sz * sh.h * (0.5 - bury);
    for (let v = 0; v < sh.pos.length; v += 3) {
      P.push(x + sh.pos[v] * sz, cy + sh.pos[v + 1] * sz, z + sh.pos[v + 2] * sz);
      N.push(sh.nrm[v], sh.nrm[v + 1], sh.nrm[v + 2]);
      T.push(sh.tone[v / 3]);
      S.push(x, cy, z, k);
    }
  });
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(P, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(N, 3));
  g.setAttribute('aTone', new THREE.Float32BufferAttribute(T, 1));
  g.setAttribute('aStone', new THREE.Float32BufferAttribute(S, 4));
  g.userData.stoneTris = tris;
  return g;
}

// the per-stone integer hash (GLSL stU / stH below, bit for bit): seed < 2^24
function stU(x) {
  x = (x ^ (x >>> 16)) >>> 0;
  x = Math.imul(x, 0x7feb352d) >>> 0;
  x = (x ^ (x >>> 15)) >>> 0;
  x = Math.imul(x, 0x846ca68b) >>> 0;
  return (x ^ (x >>> 16)) >>> 0;
}
export function stoneHash(seed, k, j) {
  return (stU((seed ^ stU(k * 16 + j)) >>> 0) >>> 8) / 16777216;
}

// stone k's pose in its clump (the vertex shader's, exactly): yaw, tilt
// about the horizontal axis at azimuth `az`, size factor, offset (jx, jz)
export function stonePose(seed, k) {
  const h1 = stoneHash(seed, k, 1), h2 = stoneHash(seed, k, 2), h3 = stoneHash(seed, k, 3), h4 = stoneHash(seed, k, 4);
  return {
    yaw: h1 * 2 * Math.PI, tilt: (h2 - 0.5) * STONE_TILT, az: h3 * 2 * Math.PI,
    s: STONE_SIZE[0] + (STONE_SIZE[1] - STONE_SIZE[0]) * h4,
    jx: (h2 - 0.5) * STONE_JIT_M, jz: (h1 - 0.5) * STONE_JIT_M,
  };
}

// stone k's centre in clump space (the shader's c' = c.xz + jitter, c.y * s)
export function stoneCentre(seed, k) {
  const [x, z, sz] = LAYOUT[k];
  const p = stonePose(seed, k);
  return { x: x + p.jx, z: z + p.jz, r: sz * 0.5 * p.s };
}

// the bake's light model at normal n (game frame) and sun visibility vis:
// light = meta.tex.light {sky, ground, sun}, L = toward the sun. -> [r, g, b]
export function stoneE(n, vis, L, light) {
  const w = 0.5 * n[1] + 0.5;
  const nl = Math.max(0, n[0] * L[0] + n[1] * L[1] + n[2] * L[2]) * vis;
  return [0, 1, 2].map((c) => light.ground[c] + (light.sky[c] - light.ground[c]) * w + light.sun[c] * nl);
}
// a face's factor on the ground's baked colour: E(face) / E(ground), per channel
export function stoneFactor(nFace, nGround, vis, L, light) {
  const a = stoneE(nFace, vis, L, light), b = stoneE(nGround, vis, L, light);
  return a.map((v, c) => v / Math.max(b[c], 1e-4));
}

const GLSL_COMMON = `
attribute vec4 aStone;
attribute vec4 aInst;     // seed, count, sun visibility, tone
attribute vec3 aNg;       // the ground's baked normal at the foot
attribute float aTone;
uniform vec3 uLtSky, uLtGnd, uLtSun, uSunL;
varying vec3 vStone;
uint stU(uint x) { x ^= x >> 16u; x *= 0x7feb352du; x ^= x >> 15u; x *= 0x846ca68bu; return x ^ (x >> 16u); }
float stH(uint seed, uint k, uint j) { return float(stU(seed ^ stU(k * 16u + j)) >> 8u) / 16777216.0; }
mat3 stRot(vec3 a, float t) {
  float c = cos(t), s = sin(t), o = 1.0 - c;
  return mat3(c + a.x * a.x * o, a.y * a.x * o + a.z * s, a.z * a.x * o - a.y * s,
              a.x * a.y * o - a.z * s, c + a.y * a.y * o, a.z * a.y * o + a.x * s,
              a.x * a.z * o + a.y * s, a.y * a.z * o - a.x * s, c + a.z * a.z * o);
}
vec3 stE(vec3 n, float vis) {
  return uLtGnd + (uLtSky - uLtGnd) * (0.5 * n.y + 0.5) + uLtSun * (vis * max(dot(n, uSunL), 0.0));
}
`;

// the pool read (gmtex.js VT_DECL_GLSL / VT_PRE_GLSL's layout: uVtLv[j] =
// (first idx row, nx, ny, tile m), idx = slot + 1, bit 15 = pool 1; slot uv =
// (x / m - tx, 1 - z / m + ty)), finest resident level first, one textureLod of
// the mip whose texel is ~texelM; vec3(-1) = no tile there. Shared with grass.js
// (the tufts' and the shells' measured colour): fn names the function.
export const POOL_UNIFORMS = ['uVtPool0', 'uVtPool1', 'uVtIdx', 'uVtLv', 'uVtPick'];
export function poolGLSL(nLv, texelM = STONE_TEXEL_M, fn = 'stGround') {
  return `
uniform sampler2DArray uVtPool0;
uniform sampler2DArray uVtPool1;
uniform usampler2D uVtIdx;
uniform vec4 uVtLv[${nLv}];
uniform vec3 uVtPick;
vec3 ${fn}(vec2 xz) {
  for (int i = 0; i < ${nLv}; i++) {
    if (float(i) >= uVtPick.z) break;
    vec4 lv = uVtLv[i];
    vec2 t = clamp(floor(xz / lv.w), vec2(0.0), lv.yz - 1.0);
    uint s = texelFetch(uVtIdx, ivec2(int(t.x), int(lv.x + t.y)), 0).r;
    if (s == 0u) continue;
    vec3 p = vec3(xz.x / lv.w - t.x, 1.0 - xz.y / lv.w + t.y, float((s & 0x7fffu) - 1u));
    float lod = max(0.0, log2(${texelM.toFixed(2)} * 512.0 / lv.w));
    return (s & 0x8000u) != 0u ? textureLod(uVtPool1, p, lod).rgb : textureLod(uVtPool0, p, lod).rgb;
  }
  return vec3(-1.0);
}
`;
}

// GLSL_BEGIN: no pool tile at the foot (g = -1) collapses the stone (not drawn, never a guessed colour)
const GLSL_BEGIN = `
#include <begin_vertex>
{
  uint sd = uint(aInst.x), sk = uint(aStone.w);
  if (aStone.w >= aInst.y) { transformed = vec3(0.0); vStone = vec3(0.0); }
  else {
    float h1 = stH(sd, sk, 1u), h2 = stH(sd, sk, 2u), h3 = stH(sd, sk, 3u), h4 = stH(sd, sk, 4u);
    float az = h3 * 6.2831853;
    mat3 R = stRot(vec3(cos(az), 0.0, sin(az)), (h2 - 0.5) * ${STONE_TILT.toFixed(3)})
      * stRot(vec3(0.0, 1.0, 0.0), h1 * 6.2831853);
    float sc = ${STONE_SIZE[0].toFixed(3)} + ${(STONE_SIZE[1] - STONE_SIZE[0]).toFixed(3)} * h4;
    vec3 c = aStone.xyz;
    vec3 c2 = vec3(c.x + (h2 - 0.5) * ${STONE_JIT_M.toFixed(3)}, c.y * sc, c.z + (h1 - 0.5) * ${STONE_JIT_M.toFixed(3)});
    transformed = R * (position - c) * sc + c2;
    mat4 mw = modelMatrix * instanceMatrix;
    vec3 nw = normalize(mat3(mw) * (R * normal));
    vec2 foot = (mw * vec4(c2.x, 0.0, c2.z, 1.0)).xz;
#ifdef STONE_VT
    vec3 g = stGround(foot);
#else
    vec3 g = vec3(-1.0);
#endif
    vStone = g * stE(nw, aInst.z) / max(stE(normalize(aNg), aInst.z), vec3(1e-4)) * (aInst.w * aTone);
    if (g.r < 0.0) transformed = vec3(0.0);
  }
}
`;

// the stone material: MeshBasic (fog, the display curve), colour = the gain;
// ground(meta, vt) wires the light + the pool (vt: GmTex or null)
export function makeStoneMaterial() {
  const U = {
    uLtSky: { value: new THREE.Vector3(0.26, 0.32, 0.41) },
    uLtGnd: { value: new THREE.Vector3(0.06, 0.056, 0.046) },
    uLtSun: { value: new THREE.Vector3(1.07, 0.95, 0.77) },
    uSunL: { value: new THREE.Vector3(0.3, 0.8, 0.5).normalize() },
  };
  const mat = new THREE.MeshBasicMaterial({ color: 0xffffff, fog: true });
  mat.userData.stone = { U, vt: null };
  mat.onBeforeCompile = (shader) => {
    const st = mat.userData.stone;
    Object.assign(shader.uniforms, U);
    let decl = GLSL_COMMON;
    if (st.vt) {
      const vu = st.vt.uniforms;
      for (const k of POOL_UNIFORMS) shader.uniforms[k] = vu[k];
      decl = '#define STONE_VT\n' + decl + poolGLSL(vu.uVtLv.value.length);
    }
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\n' + decl)
      .replace('#include <begin_vertex>', GLSL_BEGIN);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vStone;')
      .replace('vec4 diffuseColor = vec4( diffuse, opacity );', 'vec4 diffuseColor = vec4( diffuse * vStone, opacity );');
  };
  mat.customProgramCacheKey = () => 'stones' + (mat.userData.stone.vt ? 'vt' + mat.userData.stone.vt.uniforms.uVtLv.value.length : '');
  return mat;
}

// the bake's light + the pool into the material: meta.tex.light (required,
// as the terrain: no gain = an old bake, throws), meta.lighting's sun
export function stoneGround(mat, meta, vt, sunDir) {
  const lt = meta.tex.light, st = mat.userData.stone;
  if (!(lt && lt.white > 0)) throw new Error('world meta.tex.light.white missing: texture rev 3 required');
  st.U.uLtSky.value.fromArray(lt.sky);
  st.U.uLtGnd.value.fromArray(lt.ground);
  st.U.uLtSun.value.fromArray(lt.sun);
  if (sunDir) st.U.uSunL.value.fromArray(sunDir);
  st.light = lt;
  st.L = sunDir || st.U.uSunL.value.toArray();
  mat.color.setScalar(lt.white);   // linear: the terrain's gain
  st.vt = vt && vt.uniforms ? vt : null;
  mat.needsUpdate = true;
}
